import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend( localizedFormat );
import { HeaderDataHeaderModel, ShoppingBasketBaggages, ShoppingBasketBundle, ShoppingBasketBundleInfo, ShoppingBasketBundleInfoItems, ShoppingBasketDiscountObjectType, ShoppingBasketDiscountType, ShoppingBasketDiscountsModel, ShoppingBasketFlexibilization, ShoppingBasketFlexibilizationInfo, ShoppingBasketFlexibilizationInfoConcepts, ShoppingBasketFlexibilizationInfoItems, ShoppingBasketGlobickTransfers, ShoppingBasketGlobickTransfersInfo, ShoppingBasketInsurance, ShoppingBasketInsuranceAllianzFusion, ShoppingBasketOthers, ShoppingBasketOthersItems, ShoppingBasketPriority, ShoppingBasketPriorityInfoTrips, ShoppingBasketSeats, ShoppingBasketSeatsSegments, ShoppingBasketSpeqs, ShoppingBasketSpeqsSegment, ShoppingBasketTripBaggagesSegment, ShoppingBasketTripPassenger, ShoppingBasketType } from 'src/app/core/models/ib-booking-header-model';
import { Ancillarie, AncillarieSegment, GeneralItem, GenericSummaryItem, PassengerType, Trip } from 'commercial-management--digital--web-components-design--library/dist/components/layout/summary/Summary.model';
import { downgradeToAJSService } from 'ib-web-commons/utils';
import { Condition, FareFamilyCondition } from 'src/app/core/models/ib-booking-availability-transformer-model';
import { Flight, Slice, Segment } from 'src/app/core/models/ib-booking-confirmation-model';


@Injectable({providedIn: 'root'})
export class IbSummaryTransformerService {

    constructor (
        private TranslateService: TranslateService
    ) { }

    public titleCasePipe = ( input: string ): string => {
        const titleCasePipe = new TitleCasePipe();
        return titleCasePipe.transform( input );
    };

    private discountsArrayPipe = ( dicounts: ShoppingBasketDiscountType ): ShoppingBasketDiscountObjectType[] => {
        let entriesArray: any[] = [];
        Object.entries( dicounts ).forEach( ( key ) => {
            entriesArray.push( key[ 1 ] );
        } );
        return Array.from( entriesArray );
    };

    public setTrips = ( slices: Slice[], fareFamilyConditions: FareFamilyCondition ): Trip[] => {
        const trips = [];
        const setDestination = ( info: any, date: string ) => {
            const dest = {
                airport: info.name,
                city: info.city.description,
                date: dayjs(date).format('DD MMM YYYY / HH:mm')
            };
            dest[ 'terminal' ] = (info?.terminal !== null) ? info.terminal : '';
            if ( info?.cabin?.cabinDescription ) {
                dest[ 'cabin' ] = `${ this.TranslateService.instant( 'SUMMARY_CABIN_TITLE' ) } ${ info.cabin.cabinDescription }`;
            }
            return dest;
        };
        const setFlight = ( flight: Flight ) => {
            return {
                airplane: flight.aircraft.description,
                operatedBy: `${ this.TranslateService.instant( 'SUMMARY_SEGMENT_DETAILS_OPERATED' ) } ${ flight.operationalCarrier.name }`,
                title: `${ this.TranslateService.instant( 'SUMMARY_SEGMENT_DETAILS_FLIGHT_NUMBER' ) } ${ flight.marketingFlightNumber }`
            };
        };
        const setFare = ( fareFamilyConditions: FareFamilyCondition ) => {
            const mappedFamilies = fareFamilyConditions.conditions.map( ( family: Condition ) => {
                const { code, text, name } = family;
                return {
                    code,
                    text,
                    title: name
                };
            } );
            return {
                items: mappedFamilies,
                title: `${ fareFamilyConditions.cabinDescription } ${ fareFamilyConditions.commercialName }`
            };
        };
        const setDuration = ( duration: number ): string => {
            if ( duration <= 0 ) return '0';
            const hours = Math.floor( duration / 60 );
            const minutes = duration % 60;
            return `${ hours > 0 ? hours + 'h ' : '' }${ minutes > 0 ? minutes + 'min.' : '' }`;
        };

        const getSegmentsInfo = (segments:Segment[]) => {
            return segments.map((segment)=> {
                const {departure, arrival, id, duration, flight, arrivalDateTime, departureDateTime, waitingTime} = segment;

                return {
                    id,
                    routeCodeFormatted: `${ departure.city.description } (${ departure.city.code }) - ${ arrival.city.description } (${ arrival.city.code })`,
                    routeFormatted: `${ departure.city.description } - ${ arrival.city.description }`,
                    departure: setDestination( departure, departureDateTime ),
                    arrival: setDestination( arrival, arrivalDateTime ),
                    flight: setFlight( flight ),
                    duration: setDuration( duration )
                   
                };
            });
        };



        return (slices.map((slice:Slice) =>  {
            const {segments, id  }=slice;
            return {
                id,
                segments: getSegmentsInfo(segments),
                fare: setFare( fareFamilyConditions )};

        }) as any);
    };

    public setPassengers = ( passengers: ShoppingBasketTripPassenger[] ): PassengerType[] => {
        const passengerList = [];
        passengers.forEach( ( passenger: ShoppingBasketTripPassenger ) => {
            const { type, taxes, perPassengerPrice, namesList } = passenger;
            const names = namesList.map( ( name => this.titleCasePipe( name ) ) );
            const passInfo = {
                avios: null,
                fare: perPassengerPrice,
                names,
                type,
                taxes
            };
            passengerList.push( passInfo );
        } );
        return passengerList;
    };

    public setOtherItemsFromBasket = ( basket: ShoppingBasketType): GenericSummaryItem[] => {
        const others: GenericSummaryItem[] = [];
        const onHold = basket.othersModel.others.filter( ( other ) => other.type === 'ONHOLD' );
        const notOnHold = basket.othersModel.others.filter( ( other ) => other.type !== 'ONHOLD' );
        if ( basket.discountsModel.hasDiscounts ) others.push( this.setDiscountsFromBasket( basket.discountsModel ) );
        if ( basket.othersModel.hasElements && notOnHold.length > 0 ) others.push( this.setOthersFromBasket( basket.othersModel, notOnHold ) );
        if ( basket.othersModel.hasElements && onHold.length > 0) others.push( this.setOnHoldFromBasket( onHold ) );
        return others;
    };

    private setDiscountsFromBasket = ( discountsInfo: ShoppingBasketDiscountsModel): GenericSummaryItem => {
        if ( !discountsInfo.hasDiscounts ) return;
        const discountsItem: GenericSummaryItem = <GenericSummaryItem> {};
        const { discounts, totalFormated } = discountsInfo;
        const discountsArray: ShoppingBasketDiscountObjectType[] = this.discountsArrayPipe( discounts );
        // const filteredDiscounts: ShoppingBasketDiscountObjectType[] = discountsArray.filter( ( discount: ShoppingBasketDiscountObjectType ) => ( discount.type !== BkiPmtConstants.COMPONENT.DISCOUNT.VOUCHER ) );
        const items: GeneralItem[] = [];
        discountsArray.forEach( ( discount: ShoppingBasketDiscountObjectType ) => {
            const { description, discountFormatted, type } = discount;
            items.push( {
                code: type,
                price: `-${discountFormatted}`,
                description: description
            } );
        } );
        discountsItem.type = 'DISCOUNTS';
        discountsItem.total = totalFormated;
        discountsItem.items = items;
        return discountsItem;
    };

    private setOnHoldFromBasket = ( othersInfo: ShoppingBasketOthersItems[] ): GenericSummaryItem => {
        // if ( !othersInfo.hasElements && othersInfo.others.length === 0 ) return;
        if ( othersInfo.length === 0 ) return;
        const otherItems: GenericSummaryItem = <GenericSummaryItem> {};
        const onHold = othersInfo.find( ( other ) => other.type === 'ONHOLD' );
        otherItems.type = 'ONHOLD';
        otherItems.price = onHold.totalFormatted;
        otherItems.subtitle = this.TranslateService.instant( onHold.description );
        return otherItems;
    };

    private setOthersFromBasket = ( othersInfo: ShoppingBasketOthers, othersItems: ShoppingBasketOthersItems[] ): GenericSummaryItem => {
        if ( !othersInfo.hasElements && !(othersItems.length > 0) ) return;
        const otherItems: GenericSummaryItem = <GenericSummaryItem>{};
        const { total } = othersInfo;
        const items: GeneralItem[] = [];
        othersItems.forEach( ( other: ShoppingBasketOthersItems ) => {
            const { description, type, totalFormatted } = other;
            items.push( {
                code: type,
                price: totalFormatted,
                description: this.TranslateService.instant( description )
            } );
        } );
        otherItems.type = 'OTHER_CHARGES';
        otherItems.total = total.totalFormatted;
        otherItems.items = items;
        return otherItems;
    };

    public getAncillaries = (headerModel: HeaderDataHeaderModel): Ancillarie[] => {
        const ancillaries: Ancillarie[] = [];
        if(headerModel?.shoppingBasket?.baggages?.hasBaggage) ancillaries.push(this.getBaggageAncillarie(headerModel.shoppingBasket.baggages));
        if(headerModel?.shoppingBasket?.bundle?.hasBundle) ancillaries.push(this.setBundleAncillarie(headerModel.shoppingBasket.bundle));
        // if(headerModel?.shoppingBasket?.carbonFootprint?.hasCarbonFootprint) // no está activo / no se consume en bki
        if ( headerModel?.shoppingBasket?.flexibilization?.hasFlexibilization ) ancillaries.push( this.setFlexibilizationAncillarie( headerModel.shoppingBasket.flexibilization ) );
        // if(headerModel?.shoppingBasket?.globickActivities?.hasActivitiesGlobick) // no está activo / no se consume en bki
        if ( headerModel?.shoppingBasket?.globickTransfers?.hasTransfersGlobick ) ancillaries.push( this.setGlobickTransfersAncillarie( headerModel.shoppingBasket.globickTransfers ) );
        if(headerModel?.shoppingBasket?.insurance?.hasInsurance) ancillaries.push(this.getInsuranceAncillarie(headerModel.shoppingBasket.insurance));
        if(headerModel?.shoppingBasket?.priorityBoarding?.hasPriorityBoarding) ancillaries.push(this.getPriorityBoardingAncillarie(headerModel.shoppingBasket.priorityBoarding));
        if(headerModel?.shoppingBasket?.seats?.hasSeats) ancillaries.push(this.getSeatAncillarie(headerModel.shoppingBasket.seats));
        if(headerModel?.shoppingBasket?.speqs?.hasSpeqs) ancillaries.push(this.getSpeqsAncillarie(headerModel.shoppingBasket.speqs));
        // if(headerModel?.shoppingBasket?.upgrading?.hasUpgrading) // no se consume en bki
        return ancillaries;
    };

    private setFlexibilizationAncillarie = ( flexibilizationInfo: ShoppingBasketFlexibilization ): Ancillarie => {
        const setFlexibilizationItems = ( flexibilizationItems: ShoppingBasketFlexibilizationInfoItems[] ): GeneralItem[] => {
            const items = [];
            flexibilizationItems.forEach( ( flexItems: ShoppingBasketFlexibilizationInfoItems ) => {
                const { concepts } = flexItems;
                concepts.forEach( ( concept: ShoppingBasketFlexibilizationInfoConcepts ) => {
                    const { description, priceFormatted, quantity } = concept;
                    items.push( {
                        amount: quantity,
                        code: description,
                        price: priceFormatted,
                        description: description
                    } );
                } );
            } );
            return items;
        };
        const setFlexibilizationSegments = (info: ShoppingBasketFlexibilizationInfo ): AncillarieSegment[] => {
            const flexInfo: AncillarieSegment[] = [];
            const { category, items, title } = info;
            flexInfo.push( {
                id: category,
                // title: this.TranslateService.instant( title ),
                title: '',
                items: setFlexibilizationItems( items )
            } );
            return flexInfo;
        };
        const { info } = flexibilizationInfo;
        const flexibilization: Ancillarie = {
            code: 'FLEXIBILITY_ON_DEMAND',
            segments: setFlexibilizationSegments( info ),
            total: info.amount.priceFormatted
        };
        return flexibilization;
    };

    private setGlobickTransfersAncillarie = ( globickTransfers: ShoppingBasketGlobickTransfers ): Ancillarie => {
        const setTransferSegments = (info: ShoppingBasketGlobickTransfersInfo): AncillarieSegment[] => {
            const transfers: AncillarieSegment[] = [];
            const { date, transferCar, totalFormatted } = info;
            transfers.push( {
                id: `${transferCar}-${date}`,
                title: transferCar,
                items: [{
                    code: '',
                    price: totalFormatted,
                    description: date
                } ]
            } );
            return transfers;
        };
        const { info, total } = globickTransfers;
        const transfer: Ancillarie = {
            code: 'TRANSFERS',
            segments: setTransferSegments( info ),
            total: total.totalFormatted
        };
        return transfer;
    };

    private setBundleAncillarie = ( bundles: ShoppingBasketBundle ): Ancillarie => {
        const setBundleItems = ( bundleItems: ShoppingBasketBundleInfoItems[] ): GeneralItem[] => {
            const items = [];
            bundleItems.forEach( ( bundle: ShoppingBasketBundleInfoItems ) => {
                const { subtype, text } = bundle;
                items.push( {
                    amount: 1,
                    code: subtype,
                    description: text
                } );
            } );
            return items;
        };
        const setBundleSegments = (info: ShoppingBasketBundleInfo): AncillarieSegment[] => {
            const bundles: AncillarieSegment[] = [];
            const { id, items } = info;
            bundles.push( {
                id,
                title: this.TranslateService.instant('BUNDLE_SUMMARY_ALL_TRIPS_AND_PASSENGERS'),
                items: setBundleItems( items )
            } );
            return bundles;
        };
        const { info, total } = bundles;
        const bundle: Ancillarie = {
            code: 'BUNDLE' as any,
            segments: setBundleSegments( info ),
            total: total.totalFormatted
        };
        return bundle;
    };

    private getBaggageAncillarie = (baggages: ShoppingBasketBaggages): Ancillarie => {
        const baggage: Ancillarie = {
            code: 'BAGGAGE',
            segments: this.getBaggageSegments(baggages),
            total: baggages.total.totalFormatted
        };
        return baggage;
    };

    private getBaggageSegments = (baggages: ShoppingBasketBaggages): AncillarieSegment[] => {
        const ancillarieSegments: AncillarieSegment[] = [];
        baggages?.segments.forEach((segment, index) => {
            const ancillarieSegment: AncillarieSegment = {
                id: 'seg' + index,
                title: segment.origin.description + ' - ' + segment.destination.description,
                items: this.getBaggageItems(segment)
            };
            ancillarieSegments.push(ancillarieSegment);
        });
        return ancillarieSegments;
    };

    private getBaggageItems = (segment: ShoppingBasketTripBaggagesSegment): GeneralItem[] => {
        const items: GeneralItem[] = [];
        segment?.baggages.forEach(baggage => {
            const item: GeneralItem = {
                amount: baggage.quanty,
                code: baggage.type,
                price: baggage.totalFormatted,
                description: (baggage.quanty > 1 ? this.TranslateService.instant('BAGGAGE_PLURAL_TEXT') : this.TranslateService.instant('BAGGAGE_SINGULAR_TEXT')) + ' ' + baggage.description
            };
            items.push(item);
        });
        return items;
    };

    private getSeatAncillarie = (seats: ShoppingBasketSeats): Ancillarie => {
        const baggage: Ancillarie = {
            code: 'SEAT',
            segments: this.getSeatSegments(seats),
            total: seats.total.totalFormatted
        };
        return baggage;
    };

    private getSeatSegments = (seats: ShoppingBasketSeats): AncillarieSegment[] => {
        const ancillarieSegments: AncillarieSegment[] = [];
        seats?.segments.forEach((segment, index) => {
            const ancillarieSegment: AncillarieSegment = {
                id: 'seg' + index,
                title: segment.origin.description + ' - ' + segment.destination.description,
                items: this.getSeatItems(segment)
            };
            ancillarieSegments.push(ancillarieSegment);
        });
        return ancillarieSegments;
    };

    private getSeatItems = (segment: ShoppingBasketSeatsSegments): GeneralItem[] => {
        const items: GeneralItem[] = [];
        segment?.seats.forEach(seat => {
            const item: GeneralItem = {
                amount: seat.quanty,
                code: seat.type,
                price: seat.totalFormatted,
                description: (seat.quanty > 1 ? this.TranslateService.instant('SEATS_MIN_TEXT') : this.TranslateService.instant('SEAT_MIN_TEXT')) + ' ' + seat.description
            };
            items.push(item);
        });
        return items;
    };

    private getPriorityBoardingAncillarie = (priorityBoarding: ShoppingBasketPriority): Ancillarie => {
        const ancillarie: Ancillarie = {
            code: 'PRIORITY_BOARDING',
            segments: this.getPriorityBoardingSegments(priorityBoarding),
            total: priorityBoarding.info.priceFormatted
        };
        return ancillarie;
    };

    private getPriorityBoardingSegments = (priorityBoarding: ShoppingBasketPriority): AncillarieSegment[] => {
        const ancillarieSegments: AncillarieSegment[] = [];
        priorityBoarding?.info?.trips.forEach((trip, index) => {
            const ancillarieSegment: AncillarieSegment = {
                id: 'seg' + index,
                title: trip.departure.description + ' - ' + trip.arrival.description,
                items: this.getPiorityBoardingItems(trip)
            };
            ancillarieSegments.push(ancillarieSegment);
        });
        return ancillarieSegments;
    };

    private getPiorityBoardingItems = (trip: ShoppingBasketPriorityInfoTrips): GeneralItem[] => {
        const items: GeneralItem[] = [];
        const item: GeneralItem = {
            amount: trip.quantity,
            code: trip.id,
            price: trip.priceFormatted,
            description: this.TranslateService.instant('PRIORITY_BOARDING_TITLE')
        };
        items.push(item);
        return items;
    };

    private getSpeqsAncillarie = (speqs: ShoppingBasketSpeqs): Ancillarie => {
        const speq: Ancillarie = {
            code: 'SPECIAL_EQUIPMENT',
            segments: this.getSpeqsSegments(speqs),
            total: speqs.total.totalFormatted
        };
        return speq;
    };

    private getSpeqsSegments = (speqs: ShoppingBasketSpeqs): AncillarieSegment[] => {
        const ancillarieSegments: AncillarieSegment[] = [];
        speqs?.segments.forEach((segment, index) => {
            const ancillarieSegment: AncillarieSegment = {
                id: 'seg' + index,
                title: segment.origin.description + ' - ' + segment.destination.description,
                items: this.getSpeqsItems(segment)
            };
            ancillarieSegments.push(ancillarieSegment);
        });
        return ancillarieSegments;
    };

    private getSpeqsItems = (segment: ShoppingBasketSpeqsSegment): GeneralItem[] => {
        const items: GeneralItem[] = [];
        segment?.speqs.forEach(speq => {
            const item: GeneralItem = {
                amount: speq.quanty,
                code: speq.type,
                price: speq.totalFormatted,
                description: speq.description
            };
            items.push(item);
        });
        return items;
    };

    private getInsuranceAncillarie = (insurance: ShoppingBasketInsurance): Ancillarie => {
        const ancillarie: Ancillarie = {
            code: 'INSURANCE',
            type: insurance.info.type,
            total: insurance.total.totalFormatted
        };
        return ancillarie;
    };

}
downgradeToAJSService('IbSummaryTransformerService', IbSummaryTransformerService, 'ib-pmt-order-confirmation-module', ['TranslateService']);
